import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Upload, Loader2, RotateCw, CheckCircle2, Download, Ruler, Palette, ShoppingBag,
    Camera, AlertTriangle, Sparkles, QrCode, Scissors, MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import STLViewer from "@/components/STLViewer";
import { useCart } from "@/contexts/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_ID_KEY = "cherish_mini_session";
const getSessionId = () => {
    let s = localStorage.getItem(SESSION_ID_KEY);
    if (!s) { s = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`; localStorage.setItem(SESSION_ID_KEY, s); }
    return s;
};

const StepDot = ({ n, label, active, done }) => (
    <div className="flex items-center gap-2" data-testid={`step-${n}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${done ? "bg-primary text-primary-foreground border-primary" : active ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
            {done ? <CheckCircle2 className="w-4 h-4" /> : n}
        </div>
        <span className={`text-xs ${active || done ? "text-foreground" : "text-muted-foreground"} hidden md:inline`}>{label}</span>
    </div>
);

const fmtInr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BASE_OPTIONS = [
    { key: "none", label: "No Base Needed", sub: "Figurine stands on its own · free" },
    { key: "acrylic_round", label: "Acrylic Base — Round", sub: "Clear round acrylic disc · + ₹80" },
    { key: "acrylic_square", label: "Acrylic Base — Square", sub: "Clear square acrylic base · + ₹80" },
    { key: "pla", label: "PLA Base", sub: "Printed in the same material · + ₹80" },
];
const baseLabel = (key) => BASE_OPTIONS.find((b) => b.key === key)?.label || "No Base Needed";

const Row = ({ label, value, strong }) => (
    <div className="flex justify-between items-baseline text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className={strong ? "font-semibold text-primary" : ""}>{value}</span>
    </div>
);

// Indeterminate bar for waits with no real progress (2D styling), real bar for Meshy progress.
const BusyStyles = () => (
    <style>{`@keyframes miniSlide{0%{transform:translateX(-110%)}100%{transform:translateX(320%)}}`}</style>
);

const Indeterminate = ({ hint }) => (
    <div className="mt-4" data-testid="mini-busy-bar">
        <div className="h-1.5 rounded-full bg-primary/15 overflow-hidden">
            <div className="h-full w-1/3 rounded-full bg-primary" style={{ animation: "miniSlide 1.3s ease-in-out infinite" }} />
        </div>
        {hint && <div className="text-xs text-muted-foreground mt-2 text-center">{hint}</div>}
    </div>
);

const GenProgress = ({ pct }) => {
    const p = Math.max(pct || 0, 3);
    const stage = !pct || pct < 5 ? "Queued at the 3D studio…" : pct < 55 ? "Modeling the geometry…" : pct < 90 ? "Painting the textures…" : "Final touches…";
    return (
        <div className="w-full max-w-xs mt-4" data-testid="mini-gen-progress">
            <div className="h-1.5 rounded-full bg-primary/15 overflow-hidden">
                <div className="h-full rounded-full bg-primary transition-[width] duration-700" style={{ width: `${p}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-2">{stage} {pct ? `${pct}%` : ""}</div>
        </div>
    );
};

const CreateMiniature = () => {
    const navigate = useNavigate();
    const { add } = useCart();
    const sessionId = useRef(getSessionId());
    const [cfg, setCfg] = useState(null);
    const [step, setStep] = useState(1); // 1..8
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [removeBg, setRemoveBg] = useState(false);
    const [cutout, setCutout] = useState(null);
    const [cutoutBusy, setCutoutBusy] = useState(false);
    const [style, setStyle] = useState("none");
    const [previewDoc, setPreviewDoc] = useState(null); // 2D preview from backend
    const [job, setJob] = useState(null);
    const [polling, setPolling] = useState(false);
    const [wallet, setWallet] = useState(null);
    const [topup, setTopup] = useState(null); // active top-up payment doc
    const [topupUtr, setTopupUtr] = useState("");
    const [payment, setPayment] = useState(null);   // deprecated
    const [utr, setUtr] = useState("");              // deprecated
    const [sizeCm, setSizeCm] = useState(7.5);
    const [sizeUnit, setSizeUnit] = useState("cm"); // cm | mm | in
    const [customSize, setCustomSize] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [finish, setFinish] = useState("not_painted");
    const [baseOption, setBaseOption] = useState("none");
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/miniature/config").then((r) => setCfg(r.data));
    }, []);

    const refreshWallet = async () => {
        try {
            const { data } = await api.get("/wallet", { params: { session_id: sessionId.current } });
            setWallet(data);
        } catch { }
    };
    useEffect(() => { refreshWallet(); /* eslint-disable-next-line */ }, []);

    // Poll top-up status while awaiting admin verification
    useEffect(() => {
        if (!topup || topup.status === "verified" || topup.status === "rejected") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/wallet/topup/${topup.id}`);
                setTopup(data);
                if (data.status === "verified") { toast.success(`₹${data.amount_inr} credited to your wallet!`); refreshWallet(); }
            } catch { }
        }, 4000);
        return () => clearInterval(t);
    }, [topup?.id, topup?.status]);

    // Poll job status while pending / running
    useEffect(() => {
        if (!job || !polling) return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/miniature/job/${job.id}`);
                setJob(data);
                if (data.status === "success" || data.status === "error") setPolling(false);
            } catch (e) { /* keep polling */ }
        }, 3000);
        return () => clearInterval(t);
    }, [job?.id, polling]);

    // Poll payment status once submitted
    useEffect(() => {
        if (!payment || payment.status === "verified" || payment.status === "rejected") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/miniature/pay/${payment.id}`);
                setPayment(data);
            } catch { }
        }, 5000);
        return () => clearInterval(t);
    }, [payment?.id, payment?.status]);

    // Bring the QR payment box into view as soon as it's created
    useEffect(() => {
        if (!payment) return;
        const t = setTimeout(() => {
            document.querySelector('[data-testid="mini-payment-box"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
        return () => clearTimeout(t);
    }, [payment?.id]);

    const heightMm = (() => {
        const n = Number(customSize || sizeCm);
        if (!n || n <= 0) return 75;
        if (sizeUnit === "mm") return n;
        if (sizeUnit === "in") return n * 25.4;
        return n * 10; // cm → mm
    })();

    const refreshQuote = async () => {
        if (!job || !job.model_access_paid) return;
        try {
            const { data } = await api.post(`/miniature/${job.id}/quote`, null, {
                params: { height_mm: heightMm, quantity, finish, base: baseOption },
            });
            setQuote(data);
        } catch (e) {
            setQuote(null);
            toast.error(e.response?.data?.detail || "Could not calculate quote");
        }
    };

    useEffect(() => { if (step >= 6) refreshQuote(); /* eslint-disable-next-line */ }, [step, heightMm, quantity, finish, baseOption, job?.model_access_paid]);

    const runCutout = async (file) => {
        const f = file || photoFile;
        if (!f) return;
        setCutoutBusy(true);
        try {
            const fd = new FormData();
            fd.append("photo", f);
            const { data } = await api.post("/miniature/cutout", fd, {
                params: { session_id: sessionId.current },
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 200000,
            });
            setCutout(data);
            toast.success("Background removed — subject cutout ready!");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not remove background");
            setRemoveBg(false);
            setCutout(null);
        } finally { setCutoutBusy(false); }
    };

    const onToggleRemoveBg = (on) => {
        setRemoveBg(on);
        if (on && !cutout) runCutout();
        if (!on) setCutout(null);
    };

    // ----- Handlers -----
    const onPickPhoto = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
            toast.error("Please choose a JPG, PNG or WEBP image"); return;
        }
        if (f.size > 20 * 1024 * 1024) { toast.error("Photo is larger than 20 MB"); return; }
        setPhotoFile(f);
        setPhotoPreview(URL.createObjectURL(f));
        setCutout(null);
        if (removeBg) runCutout(f);
    };

    const startGeneration = async () => {
        if (!previewDoc) return toast.error("Please generate a 2D preview first");
        if (!cfg?.generator_configured) return toast.error("The 3D generator is not configured yet. Please try again later.");
        setLoading(true);
        try {
            const { data } = await api.post("/miniature/generate", null, {
                params: { preview_id: previewDoc.id, session_id: sessionId.current },
                timeout: 200000,
            });
            setJob(data);
            setPolling(true);
            setStep(4);
            refreshWallet();
        } catch (e) {
            const detail = e.response?.data?.detail || "Could not start generation";
            toast.error(detail);
            if (e.response?.status === 402) requestTopup();
        } finally { setLoading(false); }
    };

    const generate2DPreview = async () => {
        if (!photoFile && !cutout) return toast.error("Please upload a photo first");
        setLoading(true);
        try {
            let data;
            if (cutout) {
                ({ data } = await api.post("/miniature/preview", null, {
                    params: { style, cutout_id: cutout.id, session_id: sessionId.current },
                    timeout: 200000,
                }));
            } else {
                const fd = new FormData();
                fd.append("photo", photoFile);
                ({ data } = await api.post("/miniature/preview", fd, {
                    params: { style, session_id: sessionId.current },
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 200000,
                }));
            }
            setPreviewDoc(data);
            setStep(3);
            refreshWallet();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not generate 2D preview");
            if (e.response?.status === 402) requestTopup();
        } finally { setLoading(false); }
    };

    const requestTopup = async () => {
        try {
            const { data } = await api.post("/wallet/topup/request", { amount_inr: cfg?.max_topup_inr || 100 }, { params: { session_id: sessionId.current } });
            setTopup(data); setTopupUtr("");
            toast.info("Scan the QR to pay ₹" + data.amount_inr + " — then enter the UTR below.");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not start top-up"); }
    };

    const submitTopupUtr = async () => {
        if (!topup) return;
        if (topupUtr.trim().length < 6) return toast.error("Please enter a valid UTR (min 6 chars)");
        try {
            const { data: submitResp } = await api.post(`/wallet/topup/${topup.id}/submit`, { utr: topupUtr.trim() });
            const { data } = await api.get(`/wallet/topup/${topup.id}`);
            setTopup(data);
            if (submitResp.whatsapp_url) {
                window.open(submitResp.whatsapp_url, "_blank", "noopener,noreferrer");
                toast.success("UTR submitted — a WhatsApp message to admin has been opened. Send it to speed up approval.");
            } else {
                toast.success("UTR submitted — waiting for admin to verify.");
            }
        } catch (e) { toast.error(e.response?.data?.detail || "Could not submit UTR"); }
    };

    const uploadStl = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.name.toLowerCase().endsWith(".stl")) return toast.error("Please choose a .STL file");
        if (f.size > 100 * 1024 * 1024) return toast.error("STL is larger than 100 MB");
        setLoading(true);
        try {
            const fd = new FormData();
            fd.append("stl", f);
            const { data } = await api.post("/miniature/upload-stl", fd, {
                params: { session_id: sessionId.current },
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 200000,
            });
            setJob(data);
            setStep(5); // view the uploaded model in the 3D viewer first, then choose size
            toast.success("STL uploaded — check your model, then choose a print size.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "STL upload failed");
        } finally { setLoading(false); e.target.value = ""; }
    };

    const requestRegenPayment = async () => { /* deprecated — wallet handles it now */ };
    const requestApprovePayment = async () => { /* deprecated — wallet handles it now */ };

    const downloadStl = async () => {
        try {
            const { data } = await api.get(`/miniature/${job.id}/stl`, { params: { height_mm: heightMm }, responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a"); a.href = url; a.download = `miniature-${heightMm}mm.stl`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            const msg = e.response?.data ? await (e.response.data.text?.() || "STL download failed") : "STL download failed";
            toast.error(typeof msg === "string" ? msg : "STL download failed");
        }
    };

    const addToCartAndCheckout = async () => {
        if (!quote || !quote.fits_bambu_a1_mini) return toast.error("Choose a valid size first");
        setLoading(true);
        try {
            // Finalize STL to storage so the admin gets the exact printable file
            await api.post(`/miniature/${job.id}/finalize-stl`, null, { params: { height_mm: heightMm } });
            const perUnit = (quote.amount_due_inr ?? quote.total_inr) / quote.quantity;
            add({
                product_id: `mini:${job.id}`,
                product_name: `Custom 3D Miniature (${heightMm.toFixed(1)} mm)`,
                size_label: `${heightMm.toFixed(1)} mm`,
                unit_price_inr: perUnit,
                quantity: quote.quantity,
                selected_options: { Finish: finish === "painted" ? "Painted" : "Not Painted", Base: baseLabel(baseOption), Material: "PLA", Machine: "Bambu Lab A1 mini", Weight: `${quote.weight_g} g` },
                reference_photos: [],
                cod_available: !!cfg?.cod_enabled,
                miniature_job_id: job.id,
                miniature_meta: {
                    height_mm: heightMm, weight_g: quote.weight_g, dimensions_mm: quote.dimensions_mm,
                    printing_inr: quote.printing_inr, model_access_inr: quote.model_access_inr, base_inr: quote.base_inr || 0,
                    total_inr: quote.total_inr, amount_due_inr: quote.amount_due_inr ?? quote.total_inr,
                    quantity: quote.quantity, finish, base: baseOption,
                },
            });
            toast.success("Added to cart");
            navigate("/checkout");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not add to cart"); }
        finally { setLoading(false); }
    };

    const stlPreviewUrl = job?.id ? `${API_BASE}/miniature/${job.id}/stl?height_mm=75` : null;

    return (
        <div className="max-w-5xl mx-auto px-6 py-10" data-testid="create-miniature-page">
            <BusyStyles />
            <div className="text-center mb-8">
                <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Create Your Custom Miniature</h1>
                <p className="text-muted-foreground mt-2">Turn your favorite photo into a 3D miniature.</p>
            </div>
            {wallet && !wallet.is_privileged && (
                <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/20 bg-white/50 p-3" data-testid="wallet-chip">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center"><QrCode className="w-4 h-4 text-primary" /></div>
                        <div>
                            <div className="text-xs text-muted-foreground">Wallet balance</div>
                            <div className="font-display text-lg font-bold text-primary" data-testid="wallet-balance">₹{wallet.balance_inr}</div>
                        </div>
                    </div>
                    <Button onClick={requestTopup} data-testid="wallet-topup-btn" className="rounded-full h-10 px-4 bg-primary hover:bg-primary/90 text-sm">
                        Add ₹{wallet.max_topup_inr || 100} to wallet
                    </Button>
                </div>
            )}
            {wallet?.is_privileged && (
                <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-3 text-sm text-emerald-900 flex items-center gap-2" data-testid="admin-free-badge">
                    <CheckCircle2 className="w-4 h-4" /> Admin / team account — miniature generation is free.
                </div>
            )}
            {topup && topup.status !== "verified" && !wallet?.is_privileged && (
                <div className="mb-6 rounded-2xl border border-primary/40 bg-white/70 p-5" data-testid="mini-topup-box">
                    <div className="flex items-center gap-2 mb-3"><QrCode className="w-5 h-5 text-primary" /><div className="font-semibold">Pay ₹{topup.amount_inr} via UPI to top up your wallet</div></div>
                    {cfg?.qr_image_url ? (
                        <img src={cfg.qr_image_url} alt="QR" className="w-40 h-40 mx-auto rounded-xl border" />
                    ) : (
                        <div className="rounded-xl bg-primary/5 p-4 text-center text-sm text-muted-foreground">QR code will appear here — admin can set the QR image in Settings.</div>
                    )}
                    {cfg?.upi_id && <div className="text-center text-xs mt-2">UPI ID: <b>{cfg.upi_id}</b></div>}
                    {topup.status === "awaiting_utr" && (
                        <>
                            <p className="text-xs text-muted-foreground mt-3">After paying, enter your UPI transaction reference (UTR) below.</p>
                            <div className="flex gap-2 mt-2">
                                <Input placeholder="UTR / transaction reference" value={topupUtr} onChange={(e) => setTopupUtr(e.target.value)} className="rounded-xl h-11" data-testid="mini-topup-utr" />
                                <Button onClick={submitTopupUtr} data-testid="mini-topup-submit" className="rounded-full h-11 px-5 bg-primary hover:bg-primary/90">Submit</Button>
                            </div>
                        </>
                    )}
                    {topup.status === "utr_submitted" && (
                        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">Payment received — waiting for admin verification (usually a few minutes).</div>
                    )}
                </div>
            )}
            <div className="flex flex-wrap justify-between gap-3 mb-8 px-2">
                {[
                    [1, "Photo"], [2, "Style"], [3, "Preview"], [4, "3D"],
                    [5, "Approve"], [6, "STL"], [7, "Size"], [8, "Finish"], [9, "Checkout"],
                ].map(([n, label]) => (
                    <StepDot key={n} n={n} label={label} active={step === n} done={step > n} />
                ))}
            </div>

            {/* STEP 1: UPLOAD */}
            {step === 1 && (
                <div className="rounded-3xl glass p-8" data-testid="step-1-panel">
                    <div className="flex items-center gap-3 mb-4"><Camera className="w-5 h-5 text-primary" /><h2 className="font-display text-2xl font-semibold">1. Upload Your Photo</h2></div>
                    <p className="text-sm text-muted-foreground mb-4">For best results, upload a clear photo with the person's full body visible.</p>
                    <label className="block cursor-pointer rounded-2xl border-2 border-dashed border-primary/30 hover:border-primary/60 transition p-8 text-center bg-white/40">
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickPhoto} className="hidden" data-testid="mini-photo-input" />
                        {photoPreview ? (
                            <img src={photoPreview} alt="preview" className="max-h-64 mx-auto rounded-xl" />
                        ) : (
                            <div><Upload className="w-8 h-8 mx-auto text-primary mb-2" /><div className="font-semibold">Click to choose a photo</div><div className="text-xs text-muted-foreground">JPG · PNG · WEBP · up to 20 MB</div></div>
                        )}
                    </label>
                    {photoFile && (
                        <label className={`mt-3 flex items-center gap-2 text-sm cursor-pointer rounded-xl border border-border bg-white/40 p-3 ${cutoutBusy ? "opacity-70" : ""}`} data-testid="mini-remove-bg-toggle">
                            <input type="checkbox" checked={removeBg} onChange={(e) => onToggleRemoveBg(e.target.checked)} disabled={cutoutBusy} className="w-4 h-4 accent-primary" />
                            <Scissors className="w-4 h-4 text-primary" />
                            <span className="font-medium">Remove background — cut out only the subject</span>
                            {cutoutBusy && <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> cutting…</span>}
                        </label>
                    )}
                    {cutout && (
                        <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="mini-cutout-panel">
                            <div className="text-xs font-semibold text-emerald-900 mb-2 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Background removed — this cutout will be used for your miniature</div>
                            <div className="rounded-xl overflow-hidden" style={{ backgroundImage: "repeating-conic-gradient(#d6dbe3 0% 25%, #ffffff 0% 50%)", backgroundSize: "18px 18px" }}>
                                <img src={`${API_BASE}/miniature/cutout/${cutout.id}/image`} alt="Subject cutout" className="max-h-72 mx-auto" data-testid="mini-cutout-image" />
                            </div>
                        </div>
                    )}
                    <div className="mt-4 rounded-2xl bg-primary/5 border border-primary/20 p-4 text-sm">
                        <div className="font-semibold mb-1">See a sample first</div>
                        <div className="flex items-start gap-3">
                            <img src="/samples/bobblehead.png" alt="Sample bobblehead" className="w-24 h-24 object-contain rounded-lg bg-white/60" data-testid="mini-sample-img" />
                            <div className="text-muted-foreground text-xs">
                                This is a real generated bobblehead. Your miniature will look similar.
                                <div className="mt-1">Each 2D preview ₹{cfg?.stylize_inr || 20} · Each 3D generation ₹{cfg?.gen_3d_inr || 30} · STL included · Printing ₹{cfg?.print_price_per_gram || 12}/gram · Painting no extra cost.</div>
                                <a
                                    href={cfg?.whatsapp ? `https://wa.me/${cfg.whatsapp}?text=${encodeURIComponent("Hi! I need assistance with ordering my custom miniature.")}` : "/contact"}
                                    target={cfg?.whatsapp ? "_blank" : undefined}
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full bg-[#25D366]/10 text-[#128C4A] border border-[#25D366]/30 text-xs font-semibold hover:bg-[#25D366]/20 transition-colors"
                                    data-testid="mini-need-assistance-btn"
                                >
                                    <MessageCircle className="w-3.5 h-3.5" /> Need assistance? WhatsApp us
                                </a>
                            </div>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 mt-5">
                        <Button onClick={() => setStep(2)} disabled={!photoFile} data-testid="mini-continue-style" className="rounded-full h-12 bg-primary hover:bg-primary/90">
                            Choose Style →
                        </Button>
                        <label className="cursor-pointer">
                            <input type="file" accept=".stl,model/stl" onChange={uploadStl} className="hidden" data-testid="mini-upload-stl-input" />
                            <div className="rounded-full h-12 border-2 border-primary/40 text-primary flex items-center justify-center font-medium hover:bg-primary/5 transition">
                                <Upload className="w-4 h-4 mr-2" /> Or upload your own .STL
                            </div>
                        </label>
                    </div>
                </div>
            )}

            {/* STEP 2: STYLE + 2D PREVIEW GENERATION */}
            {step === 2 && (
                <div className="rounded-3xl glass p-8" data-testid="step-2-panel">
                    <div className="flex items-center gap-3 mb-4"><Palette className="w-5 h-5 text-primary" /><h2 className="font-display text-2xl font-semibold">2. Choose Your Style</h2></div>
                    <p className="text-sm text-muted-foreground mb-4">Pick a look for your 2D preview. That preview is what will guide the 3D model. Or skip stylization entirely and use your original photo.</p>
                    <div className="grid md:grid-cols-3 gap-3">
                        {[
                            { key: "none", label: "No, I'm fine", sub: "Skip styling · straight to 3D · FREE", img: "/samples/none.jpg" },
                            { key: "bobblehead", label: "Bobblehead", sub: `Photoreal caricature · ₹${cfg?.stylize_inr || 20}`, img: "/samples/bobblehead.png" },
                            { key: "cartoon", label: "Cartoon", sub: `Pixar-style cartoon · ₹${cfg?.stylize_inr || 20}`, img: "/samples/cartoon.png" },
                            { key: "chibi", label: "Chibi", sub: `Anime chibi · ₹${cfg?.stylize_inr || 20}`, img: "/samples/chibi.png" },
                            { key: "funko", label: "Funko Pop", sub: `Vinyl toy look · ₹${cfg?.stylize_inr || 20}`, img: "/samples/funko.png" },
                            { key: "brick", label: "Brick", sub: `LEGO minifig · ₹${cfg?.stylize_inr || 20}`, img: "/samples/brick.png" },
                        ].map((o) => (
                            <button key={o.key} onClick={() => setStyle(o.key)} data-testid={`style-${o.key}`}
                                className={`p-3 rounded-2xl border-2 text-left transition flex items-center gap-3 ${style === o.key ? "border-primary bg-primary/5" : "border-border bg-white/40"}`}>
                                <img src={o.img} alt="" className="w-14 h-14 rounded-xl object-cover bg-white flex-shrink-0" data-testid={`style-thumb-${o.key}`} />
                                <div className="min-w-0">
                                    <div className="font-semibold text-sm">{o.label}</div>
                                    <div className="text-xs text-muted-foreground line-clamp-2">{o.sub}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                    <Button onClick={generate2DPreview} disabled={loading} data-testid="mini-preview-btn" className="w-full mt-5 rounded-full h-12 bg-primary hover:bg-primary/90">
                        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {style === "none" ? "Uploading…" : "Generating…"}</> : <>{style === "none" ? "Continue with My Photo" : "Generate 2D Preview"} <Sparkles className="w-4 h-4" /></>}
                    </Button>
                    {loading && style !== "none" && <Indeterminate hint="Styling your photo — usually 20–60 seconds" />}
                </div>
            )}

            {/* STEP 3: 2D PREVIEW APPROVAL */}
            {step === 3 && previewDoc && (
                <div className="rounded-3xl glass p-8" data-testid="step-3-panel">
                    <div className="flex items-center gap-3 mb-4"><Sparkles className="w-5 h-5 text-primary" /><h2 className="font-display text-2xl font-semibold">3. {previewDoc.style === "none" ? "Your Photo" : "Your 2D Preview"}</h2></div>
                    <p className="text-sm text-muted-foreground mb-4">{previewDoc.style === "none" ? "This is the photo we'll send to the 3D generator." : "Happy with this look? We'll turn it into a 3D model next. Or try a different style."}</p>
                    <div className="rounded-2xl overflow-hidden bg-white/40 flex items-center justify-center" style={{ maxHeight: 480 }}>
                        <img src={`${API_BASE}/miniature/preview/${previewDoc.id}/image`} alt="2D preview" className="max-h-[480px] object-contain" data-testid="preview-2d-image" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 mt-5">
                        <Button variant="outline" onClick={() => setStep(2)} data-testid="mini-preview-retry" className="rounded-full h-12 border-primary/40 text-primary">
                            <RotateCw className="w-4 h-4" /> Try Different Style
                        </Button>
                        <Button onClick={startGeneration} disabled={loading} data-testid="mini-generate-btn" className="rounded-full h-12 bg-primary hover:bg-primary/90">
                            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <>Generate 3D Model — FREE <Sparkles className="w-4 h-4" /></>}
                        </Button>
                    </div>
                </div>
            )}

            {/* STEP 4/5: 3D GENERATING + APPROVE */}
            {(step === 4 || step === 5) && job && (
                <div className="rounded-3xl glass p-8" data-testid="step-preview-panel">
                    <div className="flex items-center gap-3 mb-4"><Sparkles className="w-5 h-5 text-primary" /><h2 className="font-display text-2xl font-semibold">4. Your 3D Miniature</h2></div>
                    {job.status === "error" ? (
                        <div className="rounded-2xl bg-white/40 aspect-video flex flex-col items-center justify-center p-6 text-center" data-testid="mini-gen-error">
                            <AlertTriangle className="w-8 h-8 text-destructive mb-3" />
                            <div className="font-semibold">3D generation failed</div>
                            <div className="text-xs text-muted-foreground mt-1 max-w-sm">{job.error || "Something went wrong while creating your model."}</div>
                            <div className="flex gap-3 mt-4">
                                <Button variant="outline" onClick={async () => { try { const { data } = await api.get(`/miniature/job/${job.id}`); setJob(data); if (data.status !== "error" && data.status !== "success") setPolling(true); } catch { toast.error("Could not refresh status"); } }} data-testid="mini-retry-btn" className="rounded-full border-primary/40 text-primary">
                                    <RotateCw className="w-4 h-4" /> Check again
                                </Button>
                                <Button variant="ghost" onClick={() => { setJob(null); setPreviewDoc(null); setPolling(false); setStep(1); }} data-testid="mini-start-over-btn" className="rounded-full">
                                    Start over (free retry)
                                </Button>
                            </div>
                        </div>
                    ) : job.status !== "success" ? (
                        <div className="rounded-2xl bg-white/40 aspect-video flex flex-col items-center justify-center" data-testid="mini-gen-loading">
                            <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
                            <div className="font-semibold">Creating your 3D miniature…</div>
                            <div className="text-xs text-muted-foreground mt-1">This usually takes 2 to 5 minutes.</div>
                            <GenProgress pct={job.progress} />
                        </div>
                    ) : (
                        <>
                            <div className="rounded-2xl overflow-hidden bg-white/40" style={{ height: 420 }}>
                                <STLViewer src={stlPreviewUrl} height={420} testId="mini-3d-stl-viewer" />
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">Drag to rotate · Scroll or pinch to zoom.</p>
                            <div className="grid md:grid-cols-2 gap-3 mt-5">
                                <Button variant="outline" onClick={() => { setJob(null); setPreviewDoc(null); setStep(2); }} data-testid="mini-regen-btn" className="rounded-full h-12 border-primary/40 text-primary">
                                    <RotateCw className="w-4 h-4" /> {cfg?.is_privileged ? "Try again (free)" : `Regenerate — ₹${(cfg?.stylize_inr || 20) + (cfg?.gen_3d_inr || 30)}`}
                                </Button>
                                <Button onClick={() => setStep(7)} data-testid="mini-approve-btn" className="rounded-full h-12 bg-primary hover:bg-primary/90">
                                    <CheckCircle2 className="w-4 h-4" /> Looks good — Choose Size →
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* STEP 6 was STL download — merged into step 7 (Size) */}

            {/* STEP 7: SIZE + BAMBU CHECK */}
            {step === 7 && (
                <div className="rounded-3xl glass p-8" data-testid="step-size-panel">
                    <div className="flex items-center gap-3 mb-4"><Ruler className="w-5 h-5 text-primary" /><h2 className="font-display text-2xl font-semibold">7. Choose Size</h2></div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[5, 7.5, 10].map((s) => (
                            <button key={s} onClick={() => { setSizeUnit("cm"); setSizeCm(s); setCustomSize(""); }}
                                data-testid={`size-${s}cm`}
                                className={`p-4 rounded-2xl border-2 transition ${sizeUnit === "cm" && sizeCm === s && !customSize ? "border-primary bg-primary/5" : "border-border bg-white/40"}`}>
                                <div className="font-semibold">{s} cm</div>
                            </button>
                        ))}
                        <button onClick={() => setCustomSize("7")} className={`p-4 rounded-2xl border-2 transition ${customSize ? "border-primary bg-primary/5" : "border-border bg-white/40"}`} data-testid="size-custom">
                            <div className="font-semibold">Custom</div>
                        </button>
                    </div>
                    {customSize !== "" && (
                        <div className="mt-4 flex gap-2 items-center">
                            <Input type="number" value={customSize} onChange={(e) => setCustomSize(e.target.value)} className="rounded-xl h-11 max-w-[140px]" data-testid="size-custom-input" />
                            <select value={sizeUnit} onChange={(e) => setSizeUnit(e.target.value)} className="rounded-xl border h-11 px-3 bg-white/40" data-testid="size-unit">
                                <option value="cm">cm</option><option value="mm">mm</option><option value="in">inches</option>
                            </select>
                            <span className="text-sm text-muted-foreground">= {heightMm.toFixed(1)} mm</span>
                        </div>
                    )}
                    {quote && (
                        <div className={`mt-4 rounded-2xl p-4 text-sm ${quote.fits_bambu_a1_mini ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-amber-50 border border-amber-200 text-amber-900"}`}>
                            {quote.fits_bambu_a1_mini ? (
                                <>✓ Compatible with Bambu Lab A1 mini · {quote.dimensions_mm.x} × {quote.dimensions_mm.y} × {quote.dimensions_mm.z} mm</>
                            ) : (
                                <><AlertTriangle className="w-4 h-4 inline mr-1" /> This size is too large for our Bambu Lab A1 mini. Max build volume 180 × 180 × 180 mm.</>
                            )}
                        </div>
                    )}
                    <Button onClick={() => setStep(8)} disabled={!quote?.fits_bambu_a1_mini} className="w-full mt-5 rounded-full h-12 bg-primary hover:bg-primary/90" data-testid="mini-size-continue">Continue →</Button>
                </div>
            )}

            {/* STEP 8: FINISH + BASE + QUANTITY */}
            {step === 8 && (
                <div className="rounded-3xl glass p-8" data-testid="step-finish-panel">
                    <div className="flex items-center gap-3 mb-4"><Palette className="w-5 h-5 text-primary" /><h2 className="font-display text-2xl font-semibold">8. Finish, Base & Quantity</h2></div>
                    <div className="text-sm font-semibold mb-2">Finish</div>
                    <div className="grid md:grid-cols-2 gap-3">
                        {[
                            { key: "not_painted", label: "Not Painted", sub: "3D printed finish · same price" },
                            { key: "painted", label: "Painted", sub: "Hand-painted finish · same price" },
                        ].map((o) => (
                            <button key={o.key} onClick={() => setFinish(o.key)} data-testid={`finish-${o.key}`}
                                className={`p-4 rounded-2xl border-2 text-left transition ${finish === o.key ? "border-primary bg-primary/5" : "border-border bg-white/40"}`}>
                                <div className="font-semibold">{o.label}</div>
                                <div className="text-xs text-muted-foreground">{o.sub}</div>
                            </button>
                        ))}
                    </div>
                    <div className="text-sm font-semibold mt-6 mb-2">Base</div>
                    <div className="grid md:grid-cols-2 gap-3">
                        {BASE_OPTIONS.map((o) => (
                            <button key={o.key} onClick={() => setBaseOption(o.key)} data-testid={`base-${o.key}`}
                                className={`p-4 rounded-2xl border-2 text-left transition ${baseOption === o.key ? "border-primary bg-primary/5" : "border-border bg-white/40"}`}>
                                <div className="font-semibold">{o.label}</div>
                                <div className="text-xs text-muted-foreground">{o.sub}</div>
                            </button>
                        ))}
                    </div>
                    <div className="mt-6 flex items-center gap-3">
                        <div className="text-sm font-semibold">Quantity</div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-9 h-9 rounded-full border border-border" data-testid="qty-dec">−</button>
                            <div className="w-10 text-center font-semibold" data-testid="qty-value">{quantity}</div>
                            <button onClick={() => setQuantity(Math.min(20, quantity + 1))} className="w-9 h-9 rounded-full border border-border" data-testid="qty-inc">+</button>
                        </div>
                    </div>
                    <Button onClick={() => setStep(9)} className="w-full mt-6 rounded-full h-12 bg-primary hover:bg-primary/90" data-testid="mini-finish-continue">Review Order →</Button>
                </div>
            )}

            {/* STEP 9: SUMMARY */}
            {step === 9 && quote && (
                <div className="rounded-3xl glass p-8" data-testid="step-summary-panel">
                    <div className="flex items-center gap-3 mb-4"><ShoppingBag className="w-5 h-5 text-primary" /><h2 className="font-display text-2xl font-semibold">9. Your Custom Miniature</h2></div>
                    <div className="grid md:grid-cols-[240px_1fr] gap-6">
                        <div className="rounded-2xl bg-white/40 aspect-square overflow-hidden">
                            <STLViewer src={stlPreviewUrl} height={260} testId="mini-summary-stl-viewer" />
                        </div>
                        <div className="space-y-2">
                            <Row label="Size" value={`${heightMm.toFixed(1)} mm`} />
                            <Row label="Dimensions" value={`${quote.dimensions_mm.x} × ${quote.dimensions_mm.y} × ${quote.dimensions_mm.z} mm`} />
                            <Row label="Machine" value="Bambu Lab A1 mini" />
                            <Row label="Material" value="PLA" />
                            <Row label="Weight" value={`${quote.weight_g} g`} />
                            <Row label="Quantity" value={quantity} />
                            <Row label="Finish" value={finish === "painted" ? "Painted" : "Not Painted"} />
                            <Row label="Base" value={baseLabel(baseOption)} />
                            <div className="border-t border-border pt-2 mt-3">
                                <Row label="Printing" value={`₹${fmtInr(quote.printing_inr)}`} />
                                <Row label="Model & STL Access" value={`₹${fmtInr(quote.model_access_inr)}`} />
                                {quote.base_inr > 0 && <Row label={`Base (${baseLabel(baseOption)})`} value={`₹${fmtInr(quote.base_inr)}`} />}
                                <Row label="Total" value={`₹${fmtInr(quote.total_inr)}`} strong />
                                {quote.is_privileged && (
                                    <div className="mt-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2 text-sm" data-testid="mini-admin-free-note">
                                        Admin / team — free for you. Amount due at checkout: <strong>₹{fmtInr(quote.amount_due_inr ?? 0)}</strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <Button onClick={addToCartAndCheckout} disabled={loading} className="w-full mt-6 rounded-full h-12 bg-primary hover:bg-primary/90" data-testid="mini-checkout-btn">
                        Continue to Checkout →
                    </Button>
                </div>
            )}
        </div>
    );
};

const PaymentBox = ({ cfg, payment, utr, setUtr, onSubmitUtr, onExecute, loading }) => {
    if (!payment) return null;
    return (
        <div className="mt-6 rounded-2xl border border-primary/30 bg-white/60 p-5" data-testid="mini-payment-box">
            <div className="flex items-center gap-2 mb-3"><QrCode className="w-5 h-5 text-primary" /><div className="font-semibold">Pay ₹{payment.amount_inr} via UPI</div></div>
            {cfg?.qr_image_url ? (
                <img src={cfg.qr_image_url} alt="QR" className="w-40 h-40 mx-auto rounded-xl border" />
            ) : (
                <div className="rounded-xl bg-primary/5 p-4 text-center text-sm text-muted-foreground">QR code will appear here — admin can set the QR image in Settings.</div>
            )}
            {cfg?.upi_id && <div className="text-center text-xs mt-2">UPI ID: <b>{cfg.upi_id}</b></div>}
            {payment.status === "awaiting_utr" && (
                <>
                    <p className="text-xs text-muted-foreground mt-3">After paying, enter your UPI transaction reference (UTR) below.</p>
                    <div className="flex gap-2 mt-2">
                        <Input placeholder="UTR / transaction reference" value={utr} onChange={(e) => setUtr(e.target.value)} className="rounded-xl h-11" data-testid="mini-utr-input" />
                        <Button onClick={onSubmitUtr} className="rounded-full h-11 px-5 bg-primary hover:bg-primary/90" data-testid="mini-utr-submit">Submit</Button>
                    </div>
                </>
            )}
            {payment.status === "utr_submitted" && (
                <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                    Payment received — waiting for admin to verify (usually a few minutes).
                </div>
            )}
            {payment.status === "verified" && (
                <>
                    <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
                        Payment verified! Tap continue to proceed.
                    </div>
                    <Button onClick={onExecute} disabled={loading} className="w-full mt-3 rounded-full h-11 bg-primary hover:bg-primary/90" data-testid="mini-payment-continue">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
                    </Button>
                </>
            )}
            {payment.status === "rejected" && (
                <div className="mt-3 rounded-xl bg-destructive/10 border border-destructive/30 p-3 text-sm">
                    Payment was rejected. Please try again.
                </div>
            )}
        </div>
    );
};

export default CreateMiniature;
